/**
 * Patient Notifications page.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationAPI } from '../../services/api';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';
import { useNotifications } from '../../contexts/NotificationsContext';

const iconByType = {
  appointment: '📅',
  prescription: '💊',
  prescription_ready: '💊',
  payment: '💳',
  payment_confirmed: '💳',
  emergency: '🚨',
  consultation_ready: '📹',
  system: 'ℹ️',
  verification: '✅',
};

const PatientNotifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const { on, decrementUnread, clearUnread } = useNotifications();

  const loadNotifications = useCallback(async () => {
    try {
      const res = await notificationAPI.list();
      setNotifications(Array.isArray(res.data) ? res.data : (res.data?.notifications || []));
    } catch {
      toast.error('Failed to load notifications.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadNotifications(); }, [loadNotifications]);

  // Re-fetch whenever any notification event arrives via WebSocket
  useEffect(() => {
    return on('*', () => { loadNotifications(); });
  }, [on, loadNotifications]);

  const handleNotificationAction = useCallback((notification) => {
    if (notification.type === 'prescription_ready') {
      if (notification.prescription_id) {
        navigate(`/patient/prescriptions/${notification.prescription_id}`);
      } else {
        navigate('/patient/prescriptions');
      }
      return;
    }

    if (notification.type === 'consultation_ready' && notification.room_id) {
      navigate(`/consultation?room=${notification.room_id}&appointment=${notification.appointment_id || ''}`);
      return;
    }

    if (notification.type === 'payment_confirmed') {
      navigate('/patient/appointments');
    }
  }, [navigate]);

  const markRead = async (id) => {
    try {
      await notificationAPI.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      decrementUnread(1);
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await notificationAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      clearUnread();
      toast.success('All notifications marked as read.');
    } catch {}
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <Layout title="Notifications" role="patient" showBack>
      <div className="p-4 pb-24">
        {unreadCount > 0 && (
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-500">{unreadCount} unread</p>
            <button onClick={markAllRead} className="text-primary-500 text-sm font-medium">
              Mark All Read
            </button>
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="skeleton h-16 rounded-2xl" />)}
          </div>
        ) : notifications.length === 0 ? (
          <div className="card text-center py-12 text-gray-400">
            <p className="text-3xl mb-2">🔔</p>
            <p className="font-medium">No notifications</p>
            <p className="text-sm mt-1">You're all caught up!</p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map(n => (
              <button
                key={n.id}
                onClick={() => {
                  if (!n.read) markRead(n.id);
                  handleNotificationAction(n);
                }}
                className={`card w-full text-left flex items-start gap-3 transition-all ${
                  !n.read ? 'bg-primary-50 border-primary-100' : 'opacity-75'
                }`}
              >
                <span className="text-xl mt-0.5">{iconByType[n.type] || '📌'}</span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${!n.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                    {n.title || n.message}
                  </p>
                  {n.message && n.title && (
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                  )}
                  {n.type === 'consultation_ready' && n.room_id && (
                    <p className="text-xs text-blue-600 font-semibold mt-1">Join Video Call →</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                  </p>
                </div>
                {!n.read && <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>}
              </button>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PatientNotifications;
